import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { base44 } from '@/api/base44Client';

async function fetchLiveKurs() {
  try {
    const res = await fetch('https://economia.awesomeapi.com.br/json/last/USD-IDR');
    const data = await res.json();
    if (data?.USDIDR?.bid) return Math.round(parseFloat(data.USDIDR.bid));
  } catch {}
  return null;
}

async function fetchLiveKursEuro() {
  try {
    const res = await fetch('https://economia.awesomeapi.com.br/json/last/EUR-IDR');
    const data = await res.json();
    if (data?.EURIDR?.bid) return Math.round(parseFloat(data.EURIDR.bid));
  } catch {}
  return null;
}

export function useKurs() {
  const queryClient = useQueryClient();

  // Fetch settings dari DB
  const { data: settingsList = [] } = useQuery({
    queryKey: ['kurs-settings'],
    queryFn: () => base44.entities.KursSettings.list('-updated_date', 1),
  });

  // Fetch live kurs USD (cache 1 jam)
  const { data: liveKurs } = useQuery({
    queryKey: ['live-kurs'],
    queryFn: fetchLiveKurs,
    staleTime: 60 * 60 * 1000,
    retry: 1,
  });

  // Fetch live kurs EUR (cache 1 jam)
  const { data: liveKursEuro } = useQuery({
    queryKey: ['live-kurs-euro'],
    queryFn: fetchLiveKursEuro,
    staleTime: 60 * 60 * 1000,
    retry: 1,
  });

  const record = settingsList[0] ?? null;
  const isManual = record?.is_manual === true;
  const isManualEuro = record?.is_manual_euro === true;

  // Nilai kurs aktual yang dipakai
  const kurs = isManual
    ? (record?.kurs_dollar ?? liveKurs ?? 17644)
    : (liveKurs ?? record?.kurs_dollar ?? 17644);

  const kursEuro = isManualEuro
    ? (record?.kurs_euro ?? liveKursEuro ?? 20414)
    : (liveKursEuro ?? record?.kurs_euro ?? 20414);

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['kurs-settings'] });

  // Simpan kurs manual USD
  const saveMutation = useMutation({
    mutationFn: async (newKurs) => {
      if (record?.id) {
        return base44.entities.KursSettings.update(record.id, {
          kurs_dollar: newKurs,
          is_manual: true,
        });
      }
      return base44.entities.KursSettings.create({
        kurs_dollar: newKurs,
        kurs_euro: liveKursEuro ?? 20414,
        is_manual: true,
        is_manual_euro: false,
      });
    },
    onSuccess: invalidate,
  });

  // Simpan kurs manual EUR
  const saveMutationEuro = useMutation({
    mutationFn: async (newKursEuro) => {
      if (record?.id) {
        return base44.entities.KursSettings.update(record.id, {
          kurs_euro: newKursEuro,
          is_manual_euro: true,
        });
      }
      return base44.entities.KursSettings.create({
        kurs_dollar: liveKurs ?? 17644,
        kurs_euro: newKursEuro,
        is_manual: false,
        is_manual_euro: true,
      });
    },
    onSuccess: invalidate,
  });

  // Reset ke live USD
  const liveMutation = useMutation({
    mutationFn: async () => {
      if (record?.id) {
        return base44.entities.KursSettings.update(record.id, {
          is_manual: false,
        });
      }
      return base44.entities.KursSettings.create({
        kurs_dollar: liveKurs ?? 17644,
        kurs_euro: liveKursEuro ?? 20414,
        is_manual: false,
        is_manual_euro: false,
      });
    },
    onSuccess: invalidate,
  });

  // Reset ke live EUR
  const liveMutationEuro = useMutation({
    mutationFn: async () => {
      if (record?.id) {
        return base44.entities.KursSettings.update(record.id, {
          is_manual_euro: false,
        });
      }
      return base44.entities.KursSettings.create({
        kurs_dollar: liveKurs ?? 17644,
        kurs_euro: liveKursEuro ?? 20414,
        is_manual: false,
        is_manual_euro: false,
      });
    },
    onSuccess: invalidate,
  });

  return {
    kurs,
    liveKurs,
    isManual,
    setManualKurs: saveMutation.mutate,
    isSaving: saveMutation.isPending,
    setLive: liveMutation.mutate,
    isSettingLive: liveMutation.isPending,

    kursEuro,
    liveKursEuro,
    isManualEuro,
    setManualKursEuro: saveMutationEuro.mutate,
    isSavingEuro: saveMutationEuro.isPending,
    setLiveEuro: liveMutationEuro.mutate,
    isSettingLiveEuro: liveMutationEuro.isPending,
  };
}